// src/services/apiPool.ts

type ApiPoolState = {
    activeApiBaseUrl: string;
    failedUntil: Record<string, number>;
  };
  
  type CreateApiPoolManagerOptions = {
    apiPool: string[];
    storageKey?: string;
    cooldownMs?: number;
    probeTimeoutMs?: number;
    healthCheckPath?: string;
    apiVersionPath?: string;
  };
  
  const DEFAULT_STORAGE_KEY = 'AIDA_API_POOL_STATE';
  
  const DEFAULT_COOLDOWN_MS = 1000 * 60 * 2; // 2 minutes
  const DEFAULT_PROBE_TIMEOUT_MS = 5000;
  const DEFAULT_HEALTH_CHECK_PATH = '/get_version';
  const DEFAULT_API_VERSION_PATH = '/api/v1';
  
  const normalizeApiBaseUrl = (url: string) => {
    return url.trim().replace(/\/+$/, '');
  };

  const toApiOrigin = (url: string) => {
    return normalizeApiBaseUrl(url).replace(/\/api\/v1$/, '');
  };

  const toApiBaseUrl = (origin: string, apiVersionPath: string) => {
    return `${toApiOrigin(origin)}${apiVersionPath}`;
  };
  
  const shuffle = <T>(array: T[]): T[] => {
    return [...array].sort(() => Math.random() - 0.5);
  };
  
  const isServerError = (status?: number) => {
    if (!status) {
      return false;
    }
  
    return status >= 500;
  };
  
  const isProbeSuccess = (status: number) => {
    return status > 0 && status < 500;
  };

  const isCoreError = (error: any) => {
    // axios timeout
    if (error?.code === 'ECONNABORTED') {
      return true;
    }
  
    // browser/network error
    if (error?.message === 'Network Error') {
      return true;
    }
  
    // fetch abort
    if (error?.name === 'AbortError') {
      return true;
    }
  
    // 5xx errors
    if (isServerError(error?.response?.status)) {
      return true;
    }
  
    return false;
  };
  
  export const createApiPoolManager = ({
    apiPool,
    storageKey = DEFAULT_STORAGE_KEY,
    cooldownMs = DEFAULT_COOLDOWN_MS,
    probeTimeoutMs = DEFAULT_PROBE_TIMEOUT_MS,
    healthCheckPath = DEFAULT_HEALTH_CHECK_PATH,
    apiVersionPath = DEFAULT_API_VERSION_PATH,
  }: CreateApiPoolManagerOptions) => {
    const normalizedPool = [
      ...new Set(
        (apiPool || [])
          .map(toApiOrigin)
          .filter(Boolean)
      ),
    ];
  
    const getDefaultState = (): ApiPoolState => ({
      activeApiBaseUrl: '',
      failedUntil: {},
    });
  
    const canUseStorage = () => {
      try {
        return !!window.sessionStorage;
      } catch {
        return false;
      }
    };
  
    const loadState = (): ApiPoolState => {
      try {
        if (!canUseStorage()) {
          return getDefaultState();
        }
  
        const raw = sessionStorage.getItem(storageKey);
  
        if (!raw) {
          return getDefaultState();
        }
  
        const parsed = JSON.parse(raw) as ApiPoolState;

        return {
          ...parsed,
          activeApiBaseUrl: parsed.activeApiBaseUrl
            ? toApiOrigin(parsed.activeApiBaseUrl)
            : '',
        };
      } catch {
        return getDefaultState();
      }
    };
  
    const saveState = (state: ApiPoolState) => {
      try {
        if (!canUseStorage()) {
          return;
        }
  
        sessionStorage.setItem(
          storageKey,
          JSON.stringify(state)
        );
      } catch {
        //
      }
    };
  
    let state = loadState();
  
    const resetCooldowns = () => {
      state.failedUntil = {};
  
      saveState(state);
    };
  
    const getAvailableApis = () => {
      const now = Date.now();
  
      return normalizedPool.filter((api) => {
        const failedUntil = state.failedUntil[api];
  
        if (!failedUntil) {
          return true;
        }
  
        return now >= failedUntil;
      });
    };
  
    const pickRandomApi = (
      excludeApis: string[] = []
    ) => {
      let availableApis = getAvailableApis().filter(
        (api) => !excludeApis.includes(api)
      );
  
      // if all apis are unavailable
      if (availableApis.length === 0) {
        resetCooldowns();
  
        availableApis = normalizedPool.filter(
          (api) => !excludeApis.includes(api)
        );
      }
  
      // if still empty
      if (availableApis.length === 0) {
        return null;
      }
  
      const shuffledApis = shuffle(availableApis);
  
      return shuffledApis[0];
    };
  
    const resolveApiBaseUrl = (origin: string) => {
      return toApiBaseUrl(origin, apiVersionPath);
    };

    const getActiveApi = async () => {
      await initialize();

      const activeOrigin = ensureActiveApi();

      return activeOrigin ? resolveApiBaseUrl(activeOrigin) : '';
    };
  
    const getNextApi = async (
      excludeApis: string[] = []
    ) => {
      await initialize();

      const excludeOrigins = excludeApis.map(toApiOrigin);
      const nextApi = pickRandomApi(excludeOrigins);
  
      if (!nextApi) {
        return null;
      }
  
      state.activeApiBaseUrl = nextApi;
  
      saveState(state);
  
      return resolveApiBaseUrl(nextApi);
    };
  
    const markApiAsFailed = (api: string) => {
      state.failedUntil[api] =
        Date.now() + cooldownMs;
  
      saveState(state);
    };

    const markApiAsHealthy = (api: string) => {
      if (state.failedUntil[api]) {
        delete state.failedUntil[api];
        saveState(state);
      }
    };

    const probeApiHealth = async (api: string): Promise<boolean> => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        probeTimeoutMs
      );

      const probeUrl = `${api}${healthCheckPath}`;

      try {
        const response = await fetch(probeUrl, {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store',
        });

        return isProbeSuccess(response.status);
      } catch {
        return false;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    const probeAllApis = async () => {
      const results = await Promise.all(
        normalizedPool.map(async (api) => ({
          api,
          healthy: await probeApiHealth(api),
        }))
      );

      for (const { api, healthy } of results) {
        if (healthy) {
          markApiAsHealthy(api);
        } else {
          markApiAsFailed(api);
        }
      }
    };

    let initializePromise: Promise<void> | null = null;

    const ensureActiveApi = () => {
      const availableApis = getAvailableApis();

      if (
        state.activeApiBaseUrl &&
        availableApis.includes(state.activeApiBaseUrl)
      ) {
        return state.activeApiBaseUrl;
      }

      state.activeApiBaseUrl = '';

      const randomApi = pickRandomApi();

      if (randomApi) {
        state.activeApiBaseUrl = randomApi;
        saveState(state);
      }

      return state.activeApiBaseUrl;
    };

    const initialize = (): Promise<void> => {
      if (!initializePromise) {
        initializePromise = probeAllApis().then(() => {
          ensureActiveApi();
        });
      }

      return initializePromise;
    };
  
    const handleRequestError = (error: any, currentApi?: string) => {
      if (!isCoreError(error)) {
        return false;
      }
  
      const failedApi = toApiOrigin(
        currentApi || state.activeApiBaseUrl
      );
  
      if (failedApi) {
        markApiAsFailed(failedApi);
      }
  
      return true;
    };
  
    const clearState = () => {
      state = getDefaultState();
      initializePromise = null;

      saveState(state);
    };
  
    return {
      initialize,
      getActiveApi,
      getNextApi,
      handleRequestError,
      clearState,
    };
  };
  
  /** Production API origins probed via `/get_version`; axios uses `{origin}/api/v1`. */
  export const API_POOL_BASE_URLS = [
    'https://api.aidasales.ir',
    'https://sobhan.api.aidasales.ir',
    'https://mohsen.api.aidasales.ir',
    'https://sahand.api.aidasales.ir',
    'https://zahra.api.aidasales.ir',
    'https://omid.api.aidasales.ir',
  ] as const;

  export const apiPoolManager = createApiPoolManager({
    apiPool: [...API_POOL_BASE_URLS],
  });